"use client"

import type { FinalExecutionOutcome } from "@near-wallet-selector/core"
import {
  useConnection as useSolanaConnection,
  useWallet as useSolanaWallet,
} from "@solana/wallet-adapter-react"
import { useWalletModal } from "@solana/wallet-adapter-react-ui"
import { clearActiveSession } from "@src/actions/auth"
import { BaseError } from "@src/components/DefuseSDK/errors/base"
import type {
  SendTransactionStellarParams,
  SendTransactionTronParams,
} from "@src/components/DefuseSDK/types/deposit"
import { extractWalletErrorCode } from "@src/components/DefuseSDK/utils/walletErrorExtractor"
import {
  useWebAuthnActions,
  useWebAuthnCurrentCredential,
  useWebAuthnUIStore,
} from "@src/features/webauthn/hooks/useWebAuthnStore"
import { teardownPrivateMode } from "@src/hooks/teardownPrivateMode"
import { useNearWallet } from "@src/providers/NearWalletProvider"
import {
  signTransactionStellar,
  submitTransactionStellar,
  useStellarWallet,
} from "@src/providers/StellarWalletProvider"
import { useTronWallet } from "@src/providers/TronWalletProvider"
import { useDisconnectingStore } from "@src/stores/useDisconnectingStore"
import type {
  SendTransactionEVMParams,
  SendTransactionSolanaParams,
  SendTransactionTonParams,
  SignAndSendTransactionsParams,
} from "@src/types/interfaces"
import { logger } from "@src/utils/logger"
import { parseTonAddress } from "@src/utils/parseTonAddress"
import { trackEvent } from "@src/utils/trackEvent"
import { useQueryClient } from "@tanstack/react-query"
import { Cell } from "@ton/ton"
import {
  useIsConnectionRestored as useIsTonConnectionRestored,
  useTonConnectModal,
  useTonConnectUI,
  useTonWallet,
} from "@tonconnect/ui-react"
import type { SendTransactionResponse } from "@tonconnect/ui-react"
import type { SendTransactionParameters } from "@wagmi/core"
import { useSearchParams } from "next/navigation"
import { useCallback, useState } from "react"
import {
  type Connector,
  useAccount,
  useConnect,
  useConnections,
  useDisconnect,
} from "wagmi"
import { disconnectWagmiConnections } from "./disconnectWagmiConnections"
import { useEVMWalletActions } from "./useEVMWalletActions"

export enum ChainType {
  Near = "near",
  EVM = "evm",
  Solana = "solana",
  WebAuthn = "webauthn",
  Ton = "ton",
  Stellar = "stellar",
  Tron = "tron",
}

export interface WalletSnapshot {
  signIn: (params: { id: ChainType; connector?: Connector }) => Promise<void>
  signOut: (params: { id: ChainType }) => Promise<void>
  sendTransaction: (params: {
    id: ChainType
    tx?:
      | SignAndSendTransactionsParams["transactions"]
      | SendTransactionEVMParams["transactions"]
      | SendTransactionSolanaParams["transactions"]
      | SendTransactionTonParams["transactions"]
      | SendTransactionStellarParams
      | SendTransactionTronParams
  }) => Promise<string | FinalExecutionOutcome[] | SendTransactionResponse>
  connectors: Connector[]
  /** Wallet facts only — no auth state (auth comes from bootstrap machine) */
  walletState: WalletState
  isLoading: boolean
  isReconnecting: boolean
  restoreStatus: "restoring" | "settled"
  interactionStatus: "idle" | "connecting" | "signing"
  signInError: string | null
  clearSignInError: () => void
}

export type WalletState = {
  chainType?: ChainType
  network?: string
  address?: string
  displayAddress?: string
  isFake: boolean
}

/**
 * Raw wallet facts and actions. No auth state — that comes from the bootstrap
 * machine which invokes session checking directly.
 *
 * Used by:
 * - `AppBootstrapProvider` (needs wallet facts to feed the machine)
 * - `useConnectWallet` (composes this with bootstrap auth state)
 */
export function useWalletSnapshot(): WalletSnapshot {
  const queryClient = useQueryClient()

  // ---- NEAR ----
  const nearWallet = useNearWallet()

  // ---- EVM ----
  const evmWalletConnect = useConnect()
  const evmWalletDisconnect = useDisconnect()
  const evmWalletAccount = useAccount()
  const evmWalletConnections = useConnections()
  const { sendTransactions } = useEVMWalletActions()

  const handleSignInViaWagmi = async ({
    connector,
  }: {
    connector: Connector
  }): Promise<void> => {
    if (!connector) {
      throw new Error("Invalid connector")
    }
    await evmWalletConnect.connectAsync({ connector })
  }
  const handleSignOutViaWagmi = async () => {
    await disconnectWagmiConnections({
      connections: evmWalletConnections,
      accountAddress: evmWalletAccount.address,
      disconnectClient: evmWalletDisconnect,
    })
  }

  // ---- Solana ----
  const { setVisible } = useWalletModal()
  const solanaWallet = useSolanaWallet()
  const solanaConnection = useSolanaConnection()
  const handleSignInViaSolanaSelector = async () => {
    setVisible(true)
  }
  const handleSignOutViaSolanaSelector = async () => {
    await solanaWallet.disconnect()
    await handleSignOutViaWagmi()
  }

  // ---- WebAuthn ----
  const currentPasskey = useWebAuthnCurrentCredential()
  const webAuthnActions = useWebAuthnActions()
  const webAuthnUI = useWebAuthnUIStore()

  // ---- TON ----
  const tonWallet = useTonWallet()
  const tonConnectModal = useTonConnectModal()
  const [tonConnectUI] = useTonConnectUI()
  const isTonConnectionRestored = useIsTonConnectionRestored()

  // ---- Stellar ----
  const {
    publicKey,
    connect,
    disconnect,
    isLoading: isStellarLoading,
  } = useStellarWallet()
  const handleSignInViaStellar = async (): Promise<void> => {
    await connect()
  }
  const handleSignOutViaStellar = async (): Promise<void> => {
    await disconnect()
  }

  // ---- Tron ----
  const tronWallet = useTronWallet()
  const handleSignInViaTron = async (): Promise<void> => {
    await tronWallet.connect()
  }
  const handleSignOutViaTron = async (): Promise<void> => {
    await tronWallet.disconnect()
  }

  // ---- Determine active wallet (last-connected wins) ----
  let walletState: WalletState = {
    chainType: undefined,
    network: undefined,
    address: undefined,
    displayAddress: undefined,
    isFake: false,
  }

  if (nearWallet.accountId != null) {
    walletState = {
      address: nearWallet.accountId,
      displayAddress: nearWallet.accountId,
      network: "near:mainnet",
      chainType: ChainType.Near,
      isFake: false,
    }
  }

  if (evmWalletAccount.address != null && evmWalletAccount.chainId) {
    walletState = {
      address: evmWalletAccount.address,
      displayAddress: evmWalletAccount.address,
      network: evmWalletAccount.chainId
        ? `eth:${evmWalletAccount.chainId}`
        : "unknown",
      chainType: ChainType.EVM,
      isFake: false,
    }
  }

  if (solanaWallet.publicKey != null) {
    walletState = {
      address: solanaWallet.publicKey.toBase58(),
      displayAddress: solanaWallet.publicKey.toBase58(),
      network: "sol:mainnet",
      chainType: ChainType.Solana,
      isFake: false,
    }
  }

  if (currentPasskey != null) {
    walletState = {
      address: currentPasskey.publicKey,
      displayAddress: currentPasskey.publicKey,
      chainType: ChainType.WebAuthn,
      isFake: false,
    }
  }

  if (tonWallet) {
    walletState = {
      address: tonWallet.account.publicKey,
      displayAddress: parseTonAddress(tonWallet.account.address),
      network: "ton",
      chainType: ChainType.Ton,
      isFake: false,
    }
  }

  if (publicKey) {
    walletState = {
      address: publicKey,
      displayAddress: publicKey,
      network: "stellar:mainnet",
      chainType: ChainType.Stellar,
      isFake: false,
    }
  }

  if (tronWallet.publicKey) {
    walletState = {
      address: tronWallet.publicKey,
      displayAddress: tronWallet.publicKey,
      network: "tron:mainnet",
      chainType: ChainType.Tron,
      isFake: false,
    }
  }

  // Impersonation override
  const impersonatedUser = useImpersonatedUser()
  if (impersonatedUser) {
    walletState = impersonatedUser
  }

  const isLoading =
    evmWalletAccount.isConnecting ||
    evmWalletAccount.isReconnecting ||
    solanaWallet.connecting ||
    isStellarLoading ||
    tronWallet.isLoading ||
    nearWallet.isInitializing ||
    !isTonConnectionRestored ||
    webAuthnUI.isSigningIn ||
    webAuthnUI.isCreating

  const restoreStatus: "restoring" | "settled" = isLoading
    ? "restoring"
    : "settled"

  const interactionStatus: "idle" | "connecting" | "signing" =
    webAuthnUI.isSigningIn || webAuthnUI.isCreating
      ? "signing"
      : evmWalletAccount.isConnecting ||
          solanaWallet.connecting ||
          isStellarLoading ||
          tronWallet.isLoading
        ? "connecting"
        : "idle"

  const { setIsDisconnecting } = useDisconnectingStore()
  const [signInError, setSignInError] = useState<string | null>(null)
  const clearSignInError = useCallback(() => setSignInError(null), [])

  return {
    async signIn(params: {
      id: ChainType
      connector?: Connector
    }): Promise<void> {
      setSignInError(null)
      if (params.id !== ChainType.WebAuthn) {
        trackEvent("wallet_connect_started", { chain: params.id })
      }

      const strategies = {
        [ChainType.Near]: () => nearWallet.connect(),
        [ChainType.EVM]: () =>
          params.connector
            ? handleSignInViaWagmi({ connector: params.connector })
            : undefined,
        [ChainType.Solana]: () => handleSignInViaSolanaSelector(),
        [ChainType.WebAuthn]: () => webAuthnUI.open(),
        [ChainType.Ton]: () => tonConnectModal.open(),
        [ChainType.Stellar]: () => handleSignInViaStellar(),
        [ChainType.Tron]: () => handleSignInViaTron(),
      }

      try {
        return await strategies[params.id]()
      } catch (error) {
        if (params.id === ChainType.WebAuthn) return

        const code = extractWalletErrorCode(error, null)
        if (code === "ERR_WALLET_CANCEL_ACTION") return

        const message =
          code === "ERR_WALLET_POPUP_BLOCKED"
            ? "Popup was blocked by your browser. Please allow popups and try again."
            : "Wallet connection failed. Please try again."

        setSignInError(message)
        logger.error("Wallet signIn failed", { chain: params.id, error })
        trackEvent("wallet_connect_failed", {
          chain: params.id,
          error_code: code ?? "unknown",
        })
      }
    },

    async signOut(params: { id: ChainType }): Promise<void> {
      setIsDisconnecting(true)
      document.documentElement.dataset.disconnecting = ""
      try {
        const strategies = {
          [ChainType.Near]: () => nearWallet.disconnect(),
          [ChainType.EVM]: () => handleSignOutViaWagmi(),
          [ChainType.Solana]: () => handleSignOutViaSolanaSelector(),
          [ChainType.WebAuthn]: () => webAuthnActions.signOut(),
          [ChainType.Ton]: () => tonConnectUI.disconnect(),
          [ChainType.Stellar]: () => handleSignOutViaStellar(),
          [ChainType.Tron]: () => handleSignOutViaTron(),
        }

        await clearActiveSession()
        teardownPrivateMode()
        try {
          localStorage.removeItem("signedInAddress")
        } catch {}
        await strategies[params.id]()

        queryClient.removeQueries({ queryKey: ["token_validation"] })
        void queryClient.invalidateQueries({ queryKey: ["swap_history"] })
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error)
        const originalError = error instanceof Error ? error : undefined
        throw new WalletDisconnectError(params.id, errorMessage, originalError)
      } finally {
        delete document.documentElement.dataset.disconnecting
        setIsDisconnecting(false)
      }
    },

    sendTransaction: async (
      params
    ): Promise<string | FinalExecutionOutcome[] | SendTransactionResponse> => {
      const strategies = {
        [ChainType.Near]: async () => {
          if (!nearWallet.accountId)
            throw new Error("NEAR wallet not connected")
          return await nearWallet.signAndSendTransactions({
            transactions:
              params.tx as SignAndSendTransactionsParams["transactions"],
          })
        },
        [ChainType.EVM]: async () =>
          await sendTransactions(params.tx as SendTransactionParameters),
        [ChainType.Solana]: async () => {
          const transaction =
            params.tx as SendTransactionSolanaParams["transactions"]
          return await solanaWallet.sendTransaction(
            transaction,
            solanaConnection.connection
          )
        },
        [ChainType.WebAuthn]: async () => {
          throw new Error("WebAuthn does not support transactions")
        },
        [ChainType.Ton]: async () => {
          if (!tonWallet) throw new Error("TON wallet not connected")
          const transaction =
            params.tx as SendTransactionTonParams["transactions"]
          const response = await tonConnectUI.sendTransaction(transaction)
          const cell = Cell.fromBoc(Buffer.from(response.boc, "base64"))[0]
          const hash = cell.hash().toString("hex")
          return hash
        },
        [ChainType.Stellar]: async () => {
          if (!publicKey) throw new Error("Stellar wallet not connected")
          const stellarParams = params.tx as SendTransactionStellarParams
          const xdrString = stellarParams.transaction.toXDR()
          const { signedTxXdr } = await signTransactionStellar(xdrString)
          const txHash = await submitTransactionStellar(signedTxXdr)
          return txHash
        },
        [ChainType.Tron]: async () => {
          if (!tronWallet) throw new Error("Tron wallet not connected")
          const tronParams = params.tx as SendTransactionTronParams
          const txHash = await tronWallet.sendTransaction({
            transaction: tronParams,
          })
          return txHash
        },
      }

      const result = await strategies[params.id]()
      if (result === undefined) {
        throw new Error(`Transaction failed for ${params.id}`)
      }
      return result
    },

    connectors: evmWalletConnect.connectors as Connector[],
    walletState,
    isLoading,
    isReconnecting: evmWalletAccount.isReconnecting,
    restoreStatus,
    interactionStatus,
    signInError,
    clearSignInError,
  }
}

function useImpersonatedUser(): WalletState | null {
  const searchParams = useSearchParams()
  const user = searchParams.get("user")
  if (user == null) return null
  const index = user.indexOf(":")
  return {
    chainType: user.slice(0, index) as ChainType,
    address: user.slice(index + 1),
    isFake: true,
  }
}

class WalletDisconnectError extends BaseError {
  chainType: ChainType
  originalError?: Error

  constructor(chainType: ChainType, message: string, originalError?: Error) {
    super("Wallet sign out error", {
      cause: originalError,
      metaMessages: [`Chain type: ${chainType}`, `Message: ${message}`],
      name: "WalletDisconnectError",
    })
    this.chainType = chainType
    this.originalError = originalError
  }
}
